import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

interface ITSReview {
  id: string; equipment_id: string; equipment_tag: string; equipment_desc: string | null;
  review_type: string; status: string; initiated_at: string | null;
  its_form_uploaded: boolean; psf_zip_uploaded: boolean;
  ai_extraction_status: string; ai_confidence_score: number | null;
  ai_gaps_found: any[]; focus_areas: string[];
  total_checks: number; completed_checks: number; failed_checks: number;
  approved_at: string | null; final_notes: string | null;
}

interface CheckItem {
  id: string; category: string; section: string | null; description: string;
  severity: string; source: string | null; status: string;
  checked_at: string | null; notes: string | null; sort_order: number;
}

interface Equipment { id: string; equipment_tag: string; description: string | null; status: string; explosion_protected: boolean; }

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  draft: { label: 'Draft', color: '#666', bg: '#f5f5f5' },
  checklist_generated: { label: 'Checklist Ready', color: '#1565c0', bg: '#e3f2fd' },
  in_review: { label: 'In Review', color: '#e65100', bg: '#fff3e0' },
  pending_approval: { label: 'Pending EEM Approval', color: '#6a1b9a', bg: '#f3e5f5' },
  approved: { label: '✓ Approved', color: '#2e7d32', bg: '#e8f5e9' },
  rejected: { label: 'Rejected', color: '#c62828', bg: '#ffebee' },
};

const CHECK_STATUS: Record<string, { icon: string; color: string }> = {
  pending: { icon: '○', color: '#bbb' },
  compliant: { icon: '✓', color: '#4caf50' },
  non_compliant: { icon: '✗', color: '#f44336' },
  not_applicable: { icon: '—', color: '#999' },
};

const CAT_ICONS: Record<string, string> = {
  document_check: '📄', ex_compliance: '⚡', mda_check: '⚠️', as4871_audit: '📋',
  risk_assessment: '🎯', conditions_of_use: '📜', overhaul_verification: '🔧',
  ai_identified: '🤖', site_specific: '🏭', its_section: '📝',
};

const REVIEW_TYPES: Record<string, string> = {
  new_introduction: 'New Introduction', returning: 'Returning to Site', post_overhaul: 'Post-Overhaul',
};

const FOCUS_OPTIONS = [
  { key: 'risk_assessments', label: 'Risk Assessments', icon: '🎯' },
  { key: 'conditions_of_use', label: 'Conditions of Use', icon: '📜' },
  { key: 'as4871', label: 'AS4871 Audit', icon: '📋' },
  { key: 'flamepath', label: 'Flamepath Inspections', icon: '🔍' },
  { key: 'earthing', label: 'Earthing Continuity', icon: '⚡' },
  { key: 'insulation', label: 'Insulation Resistance', icon: '🔌' },
];

// ── ITS LIST VIEW ──────────────────────────────────────────
const ITSList: React.FC = () => {
  const [reviews, setReviews] = useState<ITSReview[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newEqId, setNewEqId] = useState('');
  const [newType, setNewType] = useState('new_introduction');
  const [newFocus, setNewFocus] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('psf_user') || '{}');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [revRes, eqRes] = await Promise.all([
        api.get('/api/its/reviews'),
        api.get('/api/equipment/', { params: { page_size: 100 } }),
      ]);
      setReviews(revRes.data);
      setEquipment(eqRes.data.items || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newEqId) return;
    setCreating(true);
    try {
      const { data } = await api.post('/api/its/reviews', {
        equipment_id: newEqId,
        review_type: newType,
        focus_areas: newFocus,
      });
      setShowNew(false);
      navigate(`/its/${data.id}`);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to create ITS review');
    } finally { setCreating(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('psf_token'); localStorage.removeItem('psf_user'); navigate('/login');
  };

  const active = reviews.filter(r => !['approved', 'rejected'].includes(r.status));
  const completed = reviews.filter(r => r.status === 'approved');

  return (
    <div style={s.container}>
      <header style={s.header}>
        <div style={s.headerLeft}>
          <button onClick={() => navigate('/')} style={s.backBtn}>← Dashboard</button>
          <img src="/logo-white.png" alt="JMB Engineering" style={{ height: '36px' }} />
          <div style={{ width: '1px', height: '32px', background: 'rgba(255,255,255,0.2)' }} />
          <div>
            <h1 style={s.headerTitle}>PSF Platform</h1>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '1px', letterSpacing: '0.5px' }}>Plant Safety File Platform</div>
          </div>
          <span style={s.pageBadge}>ITS Reviews</span>
        </div>
        <div style={s.headerRight}>
          <span style={s.userName}>{user.name}</span>
          <button onClick={handleLogout} style={s.backBtn}>Logout</button>
        </div>
      </header>

      {/* Summary */}
      <div style={s.summaryRow}>
        <div style={{ ...s.summaryCard, borderLeft: '4px solid #ff9800' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#e65100' }}>{active.length}</div>
          <div style={s.summaryLabel}>Active Reviews</div>
        </div>
        <div style={{ ...s.summaryCard, borderLeft: '4px solid #4caf50' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#2e7d32' }}>{completed.length}</div>
          <div style={s.summaryLabel}>Completed</div>
        </div>
        <div style={{ ...s.summaryCard, borderLeft: '4px solid #2196f3' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#1565c0' }}>{reviews.length}</div>
          <div style={s.summaryLabel}>Total Reviews</div>
        </div>
      </div>

      {/* New Review */}
      <div style={{ padding: '0 32px 16px' }}>
        {!showNew ? (
          <button onClick={() => setShowNew(true)} style={s.primaryBtn}>+ New ITS Review</button>
        ) : (
          <div style={s.newForm}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>Initiate ITS Review</h3>
            <div style={s.formGrid}>
              <div style={s.field}>
                <label style={s.fLabel}>Equipment</label>
                <select value={newEqId} onChange={e => setNewEqId(e.target.value)} style={s.fInput}>
                  <option value="">Select equipment...</option>
                  {equipment.map(e => (
                    <option key={e.id} value={e.id}>{e.equipment_tag} — {e.description} {e.explosion_protected ? '(Ex)' : ''}</option>
                  ))}
                </select>
              </div>
              <div style={s.field}>
                <label style={s.fLabel}>Review Type</label>
                <select value={newType} onChange={e => setNewType(e.target.value)} style={s.fInput}>
                  <option value="new_introduction">New Introduction to Site</option>
                  <option value="returning">Returning to Site</option>
                  <option value="post_overhaul">Post-Overhaul Return</option>
                </select>
              </div>
            </div>
            <div style={{ marginTop: '12px' }}>
              <label style={s.fLabel}>EEM Focus Areas (select all that apply)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: '8px', marginTop: '6px' }}>
                {FOCUS_OPTIONS.map(fo => (
                  <button
                    key={fo.key}
                    onClick={() => setNewFocus(prev => prev.includes(fo.key) ? prev.filter(f => f !== fo.key) : [...prev, fo.key])}
                    style={{
                      ...s.focusChip,
                      background: newFocus.includes(fo.key) ? '#1a1a2e' : '#f0f0f0',
                      color: newFocus.includes(fo.key) ? '#fff' : '#555',
                    }}
                  >
                    {fo.icon} {fo.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button onClick={handleCreate} disabled={!newEqId || creating} style={s.primaryBtn}>
                {creating ? 'Creating...' : 'Generate Checklist & Start Review'}
              </button>
              <button onClick={() => setShowNew(false)} style={s.cancelBtn}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Active Reviews */}
      {active.length > 0 && (
        <div style={{ padding: '0 32px' }}>
          <h3 style={{ fontSize: '14px', color: '#666', textTransform: 'uppercase' as const, letterSpacing: '1px', margin: '0 0 12px' }}>Active Reviews</h3>
          <div style={s.cardGrid}>
            {active.map(r => {
              const sc = STATUS_CONFIG[r.status] || STATUS_CONFIG.draft;
              const pct = r.total_checks > 0 ? Math.round(((r.completed_checks + r.failed_checks) / r.total_checks) * 100) : 0;
              return (
                <div key={r.id} style={s.reviewCard} onClick={() => navigate(`/its/${r.id}`)}>
                  <div style={s.cardTop}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '18px', color: '#1a1a2e' }}>{r.equipment_tag}</div>
                      <div style={{ fontSize: '13px', color: '#888' }}>{r.equipment_desc}</div>
                    </div>
                    <span style={{ ...s.statusBadge, background: sc.bg, color: sc.color }}>{sc.label}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', margin: '8px 0' }}>
                    {REVIEW_TYPES[r.review_type] || r.review_type}
                  </div>
                  {/* Progress bar */}
                  <div style={s.progressBg}>
                    <div style={{ ...s.progressFill, width: `${pct}%`, background: r.failed_checks > 0 ? '#ff9800' : '#4caf50' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginTop: '6px' }}>
                    <span>✓ {r.completed_checks} / {r.total_checks}</span>
                    {r.failed_checks > 0 && <span style={{ color: '#f44336', fontWeight: 600 }}>✗ {r.failed_checks} NC</span>}
                    <span>{pct}%</span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' as const }}>
                    {r.its_form_uploaded && <span style={s.miniTag}>📄 ITS Form</span>}
                    {r.psf_zip_uploaded && <span style={s.miniTag}>📦 PSF Bundle</span>}
                    {r.ai_extraction_status === 'complete' && <span style={{ ...s.miniTag, background: '#e8f5e9', color: '#2e7d32' }}>🤖 AI Extracted</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Completed Reviews */}
      {completed.length > 0 && (
        <div style={{ padding: '24px 32px 0' }}>
          <h3 style={{ fontSize: '14px', color: '#666', textTransform: 'uppercase' as const, letterSpacing: '1px', margin: '0 0 12px' }}>Completed & Approved</h3>
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead><tr>
                <th style={s.th}>Equipment</th><th style={s.th}>Type</th><th style={s.th}>Checks</th>
                <th style={s.th}>Approved</th><th style={s.th}>Status</th>
              </tr></thead>
              <tbody>
                {completed.map(r => (
                  <tr key={r.id} style={{ ...s.tr, cursor: 'pointer' }} onClick={() => navigate(`/its/${r.id}`)}>
                    <td style={{ ...s.td, fontWeight: 600 }}>{r.equipment_tag} — {r.equipment_desc}</td>
                    <td style={s.td}>{REVIEW_TYPES[r.review_type]}</td>
                    <td style={s.td}>{r.completed_checks}/{r.total_checks}</td>
                    <td style={s.td}>{r.approved_at ? new Date(r.approved_at).toLocaleDateString('en-AU') : '—'}</td>
                    <td style={s.td}><span style={{ ...s.statusBadge, background: '#e8f5e9', color: '#2e7d32' }}>✓ Approved</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading && <div style={{ padding: '48px', textAlign: 'center' as const, color: '#999' }}>Loading...</div>}
      {!loading && reviews.length === 0 && !showNew && (
        <div style={{ padding: '64px 32px', textAlign: 'center' as const }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
          <h3 style={{ margin: '0 0 8px', color: '#333' }}>No ITS Reviews Yet</h3>
          <p style={{ color: '#888' }}>Start a new review when equipment arrives on site.</p>
        </div>
      )}

      {/* Footer */}
      <footer style={s.pageFooter}>
        © 2026 JMB Engineering Pty Ltd — Plant Safety File Platform
      </footer>
    </div>
  );
};


// ── ITS DETAIL VIEW (Checklist) ────────────────────────────
const ITSDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [review, setReview] = useState<ITSReview | null>(null);
  const [checklist, setChecklist] = useState<CheckItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/its/reviews/${id}`);
      setReview(data.review);
      setChecklist(data.checklist);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleCheck = async (itemId: string, status: string) => {
    setUpdating(itemId);
    try {
      const notes = status === 'non_compliant' ? prompt('Notes for non-compliance (optional):') || '' : '';
      await api.patch(`/api/its/checks/${itemId}`, { status, notes: notes || undefined });
      await load();
    } catch (err: any) { alert(err.response?.data?.detail || 'Update failed'); }
    finally { setUpdating(null); }
  };

  const handleApprove = async () => {
    if (!window.confirm('Approve this ITS review? Equipment will be marked as in-service.')) return;
    try {
      await api.post(`/api/its/reviews/${id}/approve`);
      await load();
    } catch (err: any) { alert(err.response?.data?.detail || 'Approval failed'); }
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#999' }}>Loading...</div>;
  if (!review) return <div style={{ padding: '64px', textAlign: 'center' as const }}>Review not found</div>;

  const sc = STATUS_CONFIG[review.status] || STATUS_CONFIG.draft;
  const pct = review.total_checks > 0 ? Math.round(((review.completed_checks + review.failed_checks) / review.total_checks) * 100) : 0;

  // Group checklist by category
  const grouped: Record<string, CheckItem[]> = {};
  checklist.forEach(c => {
    const key = c.section || c.category;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(c);
  });

  return (
    <div style={s.container}>
      <header style={s.header}>
        <div style={s.headerLeft}>
          <button onClick={() => navigate('/its')} style={s.backBtn}>← ITS Reviews</button>
          <img src="/logo-white.png" alt="JMB Engineering" style={{ height: '36px' }} />
          <div style={{ width: '1px', height: '32px', background: 'rgba(255,255,255,0.2)' }} />
          <div>
            <h1 style={s.headerTitle}>PSF Platform</h1>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '1px', letterSpacing: '0.5px' }}>Plant Safety File Platform</div>
          </div>
        </div>
      </header>

      <div style={{ padding: '24px 32px', maxWidth: '1200px' }}>
        {/* Review Header */}
        <div style={s.detailCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: '0 0 4px', fontSize: '24px', color: '#1a1a2e' }}>
                ITS Review — {review.equipment_tag}
              </h2>
              <p style={{ margin: '0 0 8px', color: '#666' }}>{review.equipment_desc}</p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' as const }}>
                <span style={{ ...s.statusBadge, background: sc.bg, color: sc.color }}>{sc.label}</span>
                <span style={s.typeTag}>{REVIEW_TYPES[review.review_type]}</span>
                {review.its_form_uploaded && <span style={s.miniTag}>📄 ITS Form</span>}
                {review.psf_zip_uploaded && <span style={s.miniTag}>📦 PSF Bundle</span>}
                {review.ai_extraction_status === 'complete' && (
                  <span style={{ ...s.miniTag, background: '#e8f5e9', color: '#2e7d32' }}>
                    🤖 AI Confidence: {review.ai_confidence_score}%
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Progress */}
          <div style={{ marginTop: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
              <span><strong>{review.completed_checks}</strong> compliant</span>
              {review.failed_checks > 0 && <span style={{ color: '#f44336' }}><strong>{review.failed_checks}</strong> non-compliant</span>}
              <span>{review.total_checks - review.completed_checks - review.failed_checks} pending</span>
              <span><strong>{pct}%</strong> reviewed</span>
            </div>
            <div style={{ ...s.progressBg, height: '8px' }}>
              <div style={{ ...s.progressFill, width: `${pct}%`, height: '8px', background: review.failed_checks > 0 ? '#ff9800' : '#4caf50' }} />
            </div>
          </div>

          {/* AI Gaps */}
          {review.ai_gaps_found.length > 0 && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#fff3e0', borderRadius: '8px' }}>
              <div style={{ fontWeight: 600, fontSize: '13px', color: '#e65100', marginBottom: '6px' }}>🤖 AI-Identified Issues ({review.ai_gaps_found.length})</div>
              {review.ai_gaps_found.map((g: any, i: number) => (
                <div key={i} style={{ fontSize: '13px', color: '#555', padding: '2px 0' }}>
                  • {g.description || String(g)} {g.confidence && <span style={{ color: '#999' }}>({g.confidence}% conf)</span>}
                </div>
              ))}
            </div>
          )}

          {/* Approve button */}
          {review.status === 'pending_approval' && (
            <div style={{ marginTop: '16px' }}>
              <button onClick={handleApprove} style={{ ...s.primaryBtn, background: '#4caf50', padding: '12px 32px', fontSize: '15px' }}>
                ✓ Approve ITS — Clear for Service
              </button>
            </div>
          )}

          {review.status === 'approved' && (
            <div style={{ marginTop: '16px', padding: '16px', background: '#e8f5e9', borderRadius: '8px', textAlign: 'center' as const }}>
              <div style={{ fontSize: '20px', marginBottom: '8px' }}>✅</div>
              <div style={{ fontWeight: 700, color: '#2e7d32', fontSize: '16px' }}>ITS Approved — Equipment Cleared for Service</div>
              {review.approved_at && (
                <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                  Approved {new Date(review.approved_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Checklist Sections */}
        {Object.entries(grouped).map(([section, items]) => {
          const catIcon = CAT_ICONS[items[0]?.category] || '📌';
          const sectionComplete = items.every(i => i.status !== 'pending');
          const sectionNC = items.some(i => i.status === 'non_compliant');
          return (
            <div key={section} style={{ marginTop: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <span style={{ fontSize: '16px' }}>{catIcon}</span>
                <h3 style={{ margin: 0, fontSize: '15px', color: '#1a1a2e' }}>{section}</h3>
                <span style={{ fontSize: '12px', color: '#999' }}>
                  ({items.filter(i => i.status !== 'pending').length}/{items.length})
                </span>
                {sectionComplete && !sectionNC && <span style={{ fontSize: '12px', color: '#4caf50', fontWeight: 600 }}>✓ Complete</span>}
                {sectionNC && <span style={{ fontSize: '12px', color: '#f44336', fontWeight: 600 }}>Has NC items</span>}
              </div>
              <div style={s.checklistWrap}>
                {items.map(item => {
                  const cs = CHECK_STATUS[item.status] || CHECK_STATUS.pending;
                  const isUpdating = updating === item.id;
                  return (
                    <div key={item.id} style={{
                      ...s.checkRow,
                      background: item.status === 'non_compliant' ? '#fff5f5' : item.status === 'compliant' ? '#f5fff5' : '#fff',
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: cs.color, fontSize: '16px', fontWeight: 700 }}>{cs.icon}</span>
                          <span style={{ fontSize: '14px', color: '#333' }}>{item.description}</span>
                          {item.severity === 'required' && <span style={s.reqTag}>Required</span>}
                          {item.severity === 'recommended' && <span style={s.recTag}>Recommended</span>}
                        </div>
                        {item.source && <div style={{ fontSize: '11px', color: '#999', marginTop: '2px', paddingLeft: '24px' }}>Source: {item.source}</div>}
                        {item.notes && <div style={{ fontSize: '12px', color: '#c62828', marginTop: '4px', paddingLeft: '24px' }}>📝 {item.notes}</div>}
                      </div>
                      {review.status !== 'approved' && (
                        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                          <button
                            onClick={() => handleCheck(item.id, 'compliant')}
                            disabled={isUpdating}
                            style={{ ...s.checkBtn, background: item.status === 'compliant' ? '#4caf50' : '#e8f5e9', color: item.status === 'compliant' ? '#fff' : '#4caf50' }}
                          >C</button>
                          <button
                            onClick={() => handleCheck(item.id, 'non_compliant')}
                            disabled={isUpdating}
                            style={{ ...s.checkBtn, background: item.status === 'non_compliant' ? '#f44336' : '#ffebee', color: item.status === 'non_compliant' ? '#fff' : '#f44336' }}
                          >NC</button>
                          <button
                            onClick={() => handleCheck(item.id, 'not_applicable')}
                            disabled={isUpdating}
                            style={{ ...s.checkBtn, background: item.status === 'not_applicable' ? '#9e9e9e' : '#f5f5f5', color: item.status === 'not_applicable' ? '#fff' : '#999' }}
                          >NA</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <footer style={s.pageFooter}>
        © 2026 JMB Engineering Pty Ltd — Plant Safety File Platform
      </footer>
    </div>
  );
};


// ── Router decides which view ──
const ITSDashboard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return id ? <ITSDetail /> : <ITSList />;
};

const s: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', background: '#f5f6fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', display: 'flex', flexDirection: 'column' },
  pageFooter: { marginTop: 'auto', padding: '16px 32px', background: '#1a1a2e', color: 'rgba(255,255,255,0.4)', fontSize: '12px', textAlign: 'center' as const, letterSpacing: '0.5px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 32px', background: '#1a1a2e', color: '#fff' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  headerTitle: { margin: 0, fontSize: '20px', fontWeight: 600 },
  pageBadge: { padding: '2px 10px', borderRadius: '12px', background: '#2196f3', color: '#fff', fontSize: '11px', fontWeight: 600 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  backBtn: { padding: '6px 12px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  userName: { fontWeight: 600, fontSize: '14px' },

  summaryRow: { display: 'flex', gap: '20px', padding: '24px 32px' },
  summaryCard: { flex: 1, background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  summaryLabel: { fontSize: '13px', color: '#666', marginTop: '4px', fontWeight: 600 },

  primaryBtn: { padding: '10px 24px', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 },
  cancelBtn: { padding: '10px 24px', background: '#e0e0e0', color: '#333', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },

  newForm: { background: '#fff', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  field: { display: 'flex', flexDirection: 'column' as const, gap: '4px' },
  fLabel: { fontSize: '11px', fontWeight: 600, color: '#666', textTransform: 'uppercase' as const },
  fInput: { padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' },
  focusChip: { padding: '6px 14px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 500, transition: 'all 0.15s' },

  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '16px', paddingBottom: '24px' },
  reviewCard: { background: '#fff', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', cursor: 'pointer', transition: 'box-shadow 0.15s' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  statusBadge: { padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' as const },
  typeTag: { padding: '3px 10px', borderRadius: '4px', background: '#f0f0f0', fontSize: '11px', color: '#555' },
  miniTag: { padding: '2px 8px', borderRadius: '4px', background: '#f0f4ff', color: '#1565c0', fontSize: '10px', fontWeight: 600 },
  progressBg: { width: '100%', height: '6px', background: '#e0e0e0', borderRadius: '3px', overflow: 'hidden' },
  progressFill: { height: '6px', borderRadius: '3px', transition: 'width 0.3s' },

  tableWrap: { background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: { padding: '10px 14px', textAlign: 'left' as const, fontSize: '11px', fontWeight: 600, color: '#666', textTransform: 'uppercase' as const, borderBottom: '2px solid #e0e0e0', background: '#fafafa' },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '10px 14px', fontSize: '13px', color: '#333' },

  detailCard: { background: '#fff', borderRadius: '12px', padding: '28px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  checklistWrap: { background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  checkRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #f0f0f0', gap: '12px' },
  checkBtn: { width: '36px', height: '32px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 700, fontSize: '12px' },
  reqTag: { padding: '1px 6px', borderRadius: '4px', background: '#ffebee', color: '#c62828', fontSize: '9px', fontWeight: 700 },
  recTag: { padding: '1px 6px', borderRadius: '4px', background: '#e3f2fd', color: '#1565c0', fontSize: '9px', fontWeight: 700 },
};

export default ITSDashboard;
